/**
 * Transportation Tab — Slice 3A.
 * Auto-fetches freight from DELIVERED dispatches of OCs linked to this project.
 * Shows per-dispatch rows with total_freight, advance_freight, rest_freight.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Truck, FileText, CheckCircle2 } from 'lucide-react';
import { siteProjectAPI } from '../../services/api';

const fmt = (n) => (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

export const TransportationTab = ({ project }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await siteProjectAPI.getTransportation(project.id);
      setData(r);
    } finally {
      setLoading(false);
    }
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="transportation-tab">
      <div>
        <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Truck className="w-4 h-4" /> Transportation</h4>
        <p className="text-[11px] text-gray-400">Auto-fetched from delivered dispatches of linked OCs</p>
      </div>

      {loading ? <p className="text-xs text-gray-400">Loading…</p> : !data || data.rows.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No delivered dispatches with freight yet.</p>
          <p className="text-[11px] mt-1">Transportation charges appear here once a linked OC's dispatch is marked <span className="font-semibold text-gray-500">Delivered</span>.</p>
        </div>
      ) : (
        <>
          <div className="p-4 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl max-w-sm">
            <p className="text-[10px] font-semibold text-indigo-500 uppercase tracking-wider">Total Freight</p>
            <p className="text-2xl font-bold text-indigo-700" data-testid="tr-grand-total">₹{fmt(data.grand_total)}</p>
            <p className="text-[11px] text-indigo-400 mt-1">{data.rows.length} dispatch{data.rows.length > 1 ? 'es' : ''}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
            <table className="w-full text-xs min-w-[680px]">
              <thead>
                <tr className="bg-gray-50 text-[10px] text-gray-500 uppercase">
                  <th className="text-left py-2 px-3">Dispatch #</th>
                  <th className="text-left py-2 px-3">Date</th>
                  <th className="text-left py-2 px-3">OC #</th>
                  <th className="text-left py-2 px-3">Transporter / Vehicle</th>
                  <th className="text-right py-2 px-3">Total Freight</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={i} data-testid={`tr-row-${r.dispatch_id}`} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2 px-3 font-semibold text-gray-800">
                      {r.dispatch_number}
                      <CheckCircle2 className="w-3 h-3 inline ml-1 text-emerald-500" />
                    </td>
                    <td className="py-2 px-3 text-gray-600">{r.dispatch_date}</td>
                    <td className="py-2 px-3 text-gray-700">{r.oc_number}</td>
                    <td className="py-2 px-3">
                      <div className="text-gray-700">{r.transporter_name || '-'}</div>
                      {(r.vehicle_no || r.lr_number) && <div className="text-[10px] text-gray-400">{[r.vehicle_no, r.lr_number && `LR ${r.lr_number}`].filter(Boolean).join(' · ')}</div>}
                    </td>
                    <td className="py-2 px-3 text-right font-bold text-indigo-700">₹{fmt(r.total_freight)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default TransportationTab;
